import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';


const ExpertiseManager = () => {
  const [selectedTopics, setSelectedTopics] = useState([
    { id: 1, topic: 'Workplace Stress', experienceLevel: 'lived', yearsExperience: 5 },
    { id: 2, topic: 'Bereavement', experienceLevel: 'lived', yearsExperience: 3 },
    { id: 3, topic: 'Anxiety Management', experienceLevel: 'professional', yearsExperience: 8 }
  ]);

  const [culturalTags, setCulturalTags] = useState(['LGBTQ+ Affirming', 'Multicultural']);
  const [languageTags, setLanguageTags] = useState(['English', 'Spanish']);

  const availableTopics = [
    { value: 'workplace-stress', label: 'Workplace Stress' },
    { value: 'bereavement', label: 'Bereavement' },
    { value: 'anxiety', label: 'Anxiety Management' },
    { value: 'depression', label: 'Depression Support' },
    { value: 'relationship', label: 'Relationship Issues' },
    { value: 'trauma', label: 'Trauma Recovery' },
    { value: 'addiction', label: 'Addiction Support' },
    { value: 'chronic-illness', label: 'Chronic Illness' },
    { value: 'parenting', label: 'Parenting Challenges' },
    { value: 'career', label: 'Career Transitions' }
  ];

  const experienceLevels = [
    { value: 'lived', label: 'Lived Experience' },
    { value: 'professional', label: 'Professional Training' },
    { value: 'both', label: 'Both' }
  ];

  const availableCulturalTags = [
    'LGBTQ+ Affirming',
    'Multicultural',
    'Faith-Based',
    'Veteran-Friendly',
    'Neurodiversity-Affirming',
    'Disability-Aware',
    'Indigenous-Sensitive',
    'Immigrant-Friendly'
  ];

  const availableLanguages = [
    'English',
    'Spanish',
    'Mandarin',
    'French',
    'Arabic',
    'Hindi',
    'Portuguese',
    'German',
    'Japanese',
    'Korean'
  ];

  const [newTopic, setNewTopic] = useState({
    topic: '',
    experienceLevel: '',
    yearsExperience: ''
  });

  const handleAddTopic = () => {
    if (newTopic?.topic && newTopic?.experienceLevel && newTopic?.yearsExperience) {
      const topicLabel = availableTopics?.find(t => t?.value === newTopic?.topic)?.label;
      setSelectedTopics([
        ...selectedTopics,
        {
          id: Date.now(),
          topic: topicLabel,
          experienceLevel: newTopic?.experienceLevel,
          yearsExperience: parseInt(newTopic?.yearsExperience)
        }
      ]);
      setNewTopic({ topic: '', experienceLevel: '', yearsExperience: '' });
    }
  };

  const handleRemoveTopic = (id) => {
    setSelectedTopics(selectedTopics?.filter(t => t?.id !== id));
  };

  const toggleCulturalTag = (tag) => {
    if (culturalTags?.includes(tag)) {
      setCulturalTags(culturalTags?.filter(t => t !== tag));
    } else {
      setCulturalTags([...culturalTags, tag]);
    }
  };

  const toggleLanguageTag = (lang) => {
    if (languageTags?.includes(lang)) {
      setLanguageTags(languageTags?.filter(l => l !== lang));
    } else {
      setLanguageTags([...languageTags, lang]);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
          <Icon name="Brain" size={20} className="text-secondary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Topic Expertise</h2>
          <p className="text-sm text-muted-foreground">Manage your areas of expertise and experience levels</p>
        </div>
      </div>
      <div className="space-y-6">
        <div className="bg-muted/50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Add New Topic</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Topic Area"
              placeholder="Select topic"
              options={availableTopics}
              value={newTopic?.topic}
              onChange={(value) => setNewTopic({ ...newTopic, topic: value })}
            />
            <Select
              label="Experience Type"
              placeholder="Select type"
              options={experienceLevels}
              value={newTopic?.experienceLevel}
              onChange={(value) => setNewTopic({ ...newTopic, experienceLevel: value })}
            />
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Years of Experience</label>
              <input
                type="number"
                min="0"
                max="50"
                value={newTopic?.yearsExperience}
                onChange={(e) => setNewTopic({ ...newTopic, yearsExperience: e?.target?.value })}
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Years"
              />
            </div>
          </div>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={handleAddTopic}
            className="mt-4"
            disabled={!newTopic?.topic || !newTopic?.experienceLevel || !newTopic?.yearsExperience}
          >
            Add Topic
          </Button>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">Current Expertise Areas</h3>
          <div className="space-y-3">
            {selectedTopics?.map((topic) => (
              <div key={topic?.id} className="flex items-center justify-between border border-border rounded-lg p-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Icon name="Award" size={20} className="text-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{topic?.topic}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        topic?.experienceLevel === 'professional' ?'bg-primary/10 text-primary' :'bg-secondary/10 text-secondary'
                      }`}>
                        {topic?.experienceLevel === 'professional' ? 'Professional' : 'Lived Experience'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {topic?.yearsExperience} {topic?.yearsExperience === 1 ? 'year' : 'years'}
                      </span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  onClick={() => handleRemoveTopic(topic?.id)}
                  className="text-destructive"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-sm font-semibold text-foreground mb-4">Cultural & Language Tags</h3>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-3">Cultural Competencies</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {availableCulturalTags?.map((tag) => (
                <button
                  key={tag}
                  onClick={() => toggleCulturalTag(tag)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    culturalTags?.includes(tag)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-3">Languages Spoken</label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {availableLanguages?.map((lang) => (
                <button
                  key={lang}
                  onClick={() => toggleLanguageTag(lang)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    languageTags?.includes(lang)
                      ? 'bg-secondary text-secondary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>
          </div>
        </div>

        <Button variant="default" iconName="Save" iconPosition="left" fullWidth>
          Save Expertise Profile
        </Button>
      </div>
    </div>
  );
};

export default ExpertiseManager;